import React, { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { isTauri } from '../utils/tauri';

const DISMISS_KEY = 'notification-permission-dismissed';
const DISMISS_DAYS = 7;

interface Props {
  /** 권한이 granted로 바뀐 직후 호출 (App의 subscribeToPush 배선) */
  onGranted: () => void;
  /** 상위 게이트가 다른 배너를 고른 경우 표시만 억제한다 */
  suppressed?: boolean;
}

/**
 * 알림 권한 요청 배너 (로그인 상태에서만 마운트)
 *
 * 로그인 성공 핸들러의 자동 requestPermission은 로그인 API await 이후라
 * iOS Safari/PWA에서 user gesture(transient activation)가 만료돼 무시될 수 있다.
 * 이 배너의 버튼 클릭은 확실한 gesture이므로 프롬프트가 항상 뜬다.
 */
export const NotificationPermissionBanner: React.FC<Props> = ({ onGranted, suppressed = false }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isTauri()) return;
    // iOS 비-standalone 등 Notification API 자체가 없으면 안내 불가 (홈 화면 추가는 PwaInstallBanner 담당)
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;

    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - Number(dismissedAt);
      if (elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
    }

    setShow(true);
  }, []);

  const handleEnable = async () => {
    try {
      // user gesture 안에서 동기적으로 호출해야 iOS에서 프롬프트가 뜬다 (선행 await 금지)
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        onGranted();
      } else if (permission === 'denied') {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } catch (err) {
      console.warn('[Notification] Permission request failed:', err);
    }
    setShow(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show || suppressed) return null;

  return (
    <div
      className="fixed bottom-24 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 rounded-xl shadow-lg border overflow-hidden bg-white border-slate-200"
      style={{ animation: 'slideUp 0.3s ease-out' }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="px-4 py-3">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-brand-600 shrink-0" />
            <span className="text-sm font-semibold text-gray-900">알림 허용 안내</span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-2 -m-1.5 rounded-lg hover:bg-black/10 transition-colors shrink-0 text-gray-400 hover:text-gray-600"
            title="닫기"
          >
            <X size={14} />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          앱을 사용하지 않을 때도 새 메시지 알림을 받으려면 알림을 허용해 주세요.
        </p>
        <button
          onClick={handleEnable}
          className="w-full px-3 py-2 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          알림 켜기
        </button>
      </div>
    </div>
  );
};
